import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Globe, Check } from "lucide-react";
import { toast } from "sonner";
import { PageHero, fadeUp } from "../components/sections";
import SeoHead from "../components/SeoHead";
import { apiClient } from "../lib/site";
import { getUtm } from "../lib/utm";

const SERVICES_OPTIONS = [
  "Custom Software Development",
  "Web & Mobile Applications",
  "Cloud & DevSecOps",
  "AI & Data Engineering",
  "Quality Engineering",
  "Product & UX Design",
  "Dedicated Engineering Teams",
  "Legacy Modernization",
  "Other / Not sure yet",
];

const BUDGET_OPTIONS = [
  "Under £40K",
  "£40K – £120K",
  "£120K – £400K",
  "£400K – £1.2M",
  "£1.2M+",
  "Not sure yet",
];

export default function ContactPage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: "",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/contact", { ...form, utm: getUtm() });
      toast.success("Message received. We'll be in touch within one business day.");
      setSuccess(true);
      setForm({ name: "", email: "", company: "", phone: "", service: "", budget: "", message: "" });
    } catch (err) {
      toast.error("Could not send message. Please try again or email us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SeoHead
        title="Contact"
        description="One conversation, one architect, one honest read on what it would take. Tell us what you're trying to build — info@elitesolutionshub.com."
        path="/contact"
      />
      <PageHero
        overline="Contact"
        title={<>Tell us what you're trying to build.</>}
        intro="One conversation. One architect. One honest read on what it would take. No proposal templates. No filler decks."
        testId="contact-hero"
      />

      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 lg:gap-24">
          {/* LEFT */}
          <aside className="lg:col-span-4 space-y-12">
            <div>
              <p className="overline mb-4">Reach Us Directly</p>
              <a
                href="mailto:info@elitesolutionshub.com"
                className="font-serif-display text-2xl md:text-3xl text-white font-light hover:text-zinc-300 transition flex items-center gap-3 leading-snug"
                data-testid="contact-email-link"
              >
                <Mail size={20} strokeWidth={1.5} style={{ color: "#C2533B" }} />
                info@elitesolutionshub.com
              </a>
            </div>
            <div>
              <p className="overline mb-4">Where we operate</p>
              <p className="text-zinc-300 text-base flex items-center gap-3">
                <Globe size={18} strokeWidth={1.5} style={{ color: "#C2533B" }} />
                London, UK · Headquarters
              </p>
              <p className="mt-3 text-zinc-500 text-sm leading-relaxed">
                Practitioners across the UK, Europe and the Americas. We work in your time zone,
                not ours.
              </p>
            </div>
            <div className="border-t border-white/10 pt-8">
              <p className="overline mb-4">What happens next</p>
              <ul className="space-y-4 text-zinc-400 text-sm leading-relaxed">
                <li className="flex gap-3">
                  <span style={{ color: "#C2533B" }}>01</span>
                  We respond within one business day with a 25-minute slot.
                </li>
                <li className="flex gap-3">
                  <span style={{ color: "#C2533B" }}>02</span>
                  A practitioner—not a salesperson—joins the call to listen.
                </li>
                <li className="flex gap-3">
                  <span style={{ color: "#C2533B" }}>03</span>
                  Within five business days, we send a written read of the engagement.
                </li>
              </ul>
            </div>
          </aside>

          {/* RIGHT FORM */}
          <div className="lg:col-span-8">
            {success ? (
              <motion.div
                {...fadeUp}
                data-testid="contact-success"
                className="border border-[#27272A] bg-[#0A0A0C] p-12"
              >
                <div className="w-12 h-12 border border-[#C2533B] flex items-center justify-center mb-6">
                  <Check size={20} strokeWidth={1.5} style={{ color: "#C2533B" }} />
                </div>
                <h3 className="font-serif-display text-3xl md:text-4xl text-white font-light mb-4">
                  Message received.
                </h3>
                <p className="text-zinc-400 text-base leading-relaxed max-w-md">
                  A practitioner from our team will be in touch within one business day. In the
                  meantime, you might enjoy our latest field notes.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  className="btn-secondary mt-8"
                  data-testid="contact-send-another"
                >
                  Send another message
                </button>
              </motion.div>
            ) : (
              <form onSubmit={submit} className="space-y-8" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div>
                    <label className="overline mb-3 block" htmlFor="contact-name">Name *</label>
                    <input
                      id="contact-name"
                      data-testid="contact-name-input"
                      className="input-line"
                      placeholder="Your full name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="overline mb-3 block" htmlFor="contact-email">Email *</label>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      className="input-line"
                      placeholder="you@company.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <label className="overline mb-3 block" htmlFor="contact-company">Company</label>
                    <input
                      id="contact-company"
                      data-testid="contact-company-input"
                      className="input-line"
                      placeholder="Where you work"
                      value={form.company}
                      onChange={(e) => setForm({ ...form, company: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="overline mb-3 block" htmlFor="contact-phone">Phone</label>
                    <input
                      id="contact-phone"
                      data-testid="contact-phone-input"
                      className="input-line"
                      placeholder="Optional"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="overline mb-3 block" htmlFor="contact-service">Service of interest</label>
                    <select
                      id="contact-service"
                      data-testid="contact-service-input"
                      className="input-line cursor-pointer"
                      value={form.service}
                      onChange={(e) => setForm({ ...form, service: e.target.value })}
                      style={{ background: "#050505" }}
                    >
                      <option value="">Select…</option>
                      {SERVICES_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="overline mb-3 block" htmlFor="contact-budget">Budget range</label>
                    <select
                      id="contact-budget"
                      data-testid="contact-budget-input"
                      className="input-line cursor-pointer"
                      value={form.budget}
                      onChange={(e) => setForm({ ...form, budget: e.target.value })}
                      style={{ background: "#050505" }}
                    >
                      <option value="">Select…</option>
                      {BUDGET_OPTIONS.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="overline mb-3 block" htmlFor="contact-message">
                    Tell us what you're trying to build *
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    className="input-line resize-none"
                    rows={6}
                    placeholder="The platform, the problem, the deadline, the constraint."
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    required
                  />
                </div>
                <div className="pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn-primary disabled:opacity-50"
                    data-testid="contact-submit-button"
                  >
                    {submitting ? "Sending…" : "Send Message"}{" "}
                    <ArrowUpRight size={14} strokeWidth={1.8} />
                  </button>
                  <p className="mt-4 text-zinc-600 text-xs">
                    By submitting, you consent to us contacting you about your inquiry. We don't
                    share your information.
                  </p>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
